import { Button } from "@components/ui/Button";
import Icon from "@components/ui/Icon";
import { useSearch } from "@helpers/context/SearchContext";
import { useBlockScroll } from "@helpers/hooks/useBlockScroll";
import { Params, searchURL } from "@helpers/searchURL";
import { Trans } from "@helpers/translate";
import classNames from "classnames";
import { useRouter } from "next/router";
import React, { FC, ReactNode } from "react";

interface FilterItem {
  onClick?: () => void;
  isActive?: boolean;
  filters: Params;
  children?: ReactNode;
}

const FilterItem: FC<FilterItem> = ({
  children,
  filters,
  isActive = false,
}) => {
  const router = useRouter();
  const { setOpenFilter } = useSearch();

  const wrapperClass = classNames(
    "mx-1 my-0.5 p-2",
    "hover:bg-gray-100 rounded cursor-pointer whitespace-nowrap",
    "flex items-center",
    {
      "text-black": !isActive,
      "text-secondary": isActive,
    }
  );

  const checkboxClass = classNames(
    "flex items-center border p-0.5 rounded-full text-gray-400 mr-2",
    {
      "bg-white": !isActive,
      "border-secondary bg-secondary text-white": isActive,
    }
  );

  return (
    <div
      className={wrapperClass}
      onClick={() => {
        router.push(searchURL(filters));
        setOpenFilter(undefined);
      }}
    >
      <div className={checkboxClass}>
        <Icon
          icon="check"
          size="xs"
          className={isActive ? "text-white" : "invisible"}
        />
      </div>
      {children}
    </div>
  );
};

interface ModalProps {
  setIsOpen: () => void;
  onReset?: () => void;
  onApply?: () => void;
  id: string;
  value?: string;
  lengthyContent?: boolean;
  children?: ReactNode;
}

const Modal: FC<ModalProps> = ({
  children,
  id,
  value,
  setIsOpen,
  onReset,
  onApply,
  lengthyContent,
}) => {
  useBlockScroll();
  const router = useRouter();
  const { filters } = useSearch();

  const wrapperClass = classNames(
    "z-10 bg-white sm:w-82",
    "md:relative md:mt-1", // desktop
    "max-md:fixed w-full mx-min-w-full max-md:bottom-0 max-md:right-0 left-0  max-md:rounded-t-xl max-md:py-6 ", // mobile
    "border shadow-xl max-md:shadow-lg-inverted p-2 rounded-lg",
    {
      "-mb-4": value,
    },
    { "max-md:h-1/2 max-md:overflow-y-auto": lengthyContent }
  );

  return (
    <div className="z-10 fixed md:absolute">
      <button
        onClick={() => setIsOpen()}
        tabIndex={-1}
        className="fixed inset-0 h-full w-full cursor-default bg-gray-400 bg-opacity-30"
      ></button>
      <div className={wrapperClass} style={{ minWidth: "max-content" }}>
        {children}
        {(value || onApply) && (
          <div className="mt-2 p-1 pt-3 border-t text-right">
            {value && (
              <div
                onClick={() => {
                  setIsOpen();
                  if (onReset) {
                    onReset();
                  } else {
                    // Clears filter value to null and resets pagination to page 1 when "Clear" button is clicked.
                    router.push(searchURL({ ...filters, [id]: null, page: 1 }));
                  }
                }}
                className="text-gray-500 hover:underline cursor-pointer inline-block"
              >
                <Trans id="search.filter.clear">Clear</Trans>
              </div>
            )}
            {onApply && (
              <Button
                variant="black"
                size="md"
                className="py-1 px-6 inline-block ml-4 md:text-sm md:px-4"
                onClick={onApply}
              >
                <Trans id="search.filter.apply">Apply</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface FilterProps {
  title?: string;
  value?: string;
  id: string;
  onReset?: () => void;
  onApply?: () => void;
  disabled?: boolean;
  lengthyContent?: boolean;
  children?: ReactNode;
}

export const Filter: FC<FilterProps> & { Item: typeof FilterItem } = ({
  onReset,
  onApply,
  children,
  title,
  value,
  id,
  disabled,
  lengthyContent,
}) => {
  const { openFilter, setOpenFilter } = useSearch();
  const isOpen = openFilter === id;

  const buttonClass = classNames(
    "px-4 py-3 rounded cursor-pointer mr-4 text-center border",
    "whitespace-nowrap truncate",
    {
      "hover:border-secondary hover:text-secondary": !value,
      "bg-secondary text-white border-secondary": value,
      "opacity-50": disabled,
    }
  );

  const setIsOpen = () => setOpenFilter(isOpen ? undefined : id);
  return (
    <div className="relative">
      <div className={buttonClass} onClick={() => !disabled && setIsOpen()}>
        {value || title}
      </div>
      {isOpen && (
        <Modal
          lengthyContent={lengthyContent}
          id={id}
          value={value}
          setIsOpen={() => setIsOpen()}
          onReset={onReset}
          onApply={onApply}
        >
          {children}
        </Modal>
      )}
    </div>
  );
};

Filter.Item = FilterItem;
